import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import RecommendForYou from '../components/Recommended';
import { Link } from 'react-router-dom';
import Navbar from '../components/navbar';
import Footer from '../components/footer';

const CartPage = () => {
  const [cartCount, setCartCount] = useState(0);
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    const items = JSON.parse(localStorage.getItem('cartItems')) || [];
    setCartItems(items);
  }, []);

  const removeItem = (index) => {
    const newCartItems = [...cartItems];
    newCartItems.splice(index, 1);
    setCartItems(newCartItems);
    localStorage.setItem('cartItems', JSON.stringify(newCartItems));
  };

  const updateQuantity = (index, quantity) => {
    const newCartItems = [...cartItems];
    newCartItems[index].quantity = quantity;
    setCartItems(newCartItems);
    localStorage.setItem('cartItems', JSON.stringify(newCartItems));
  };

  const calculateSubtotal = () => {
    const largeBoxItems = cartItems.filter(item => item.orderType === "Large Box");
    const smallBoxItems = cartItems.filter(item => item.orderType === "Small Box");

    // If there are any Large Box items, set the total to $99
    if (largeBoxItems.length > 0) {
      return { label: 'Large Box Subscription Plan', total: '99.00', isSubscription: true };
    }

    // If there are any Small Box items, set the total to $59
    if (smallBoxItems.length > 0) {
      return { label: 'Small Box Subscription Plan', total: '59.00', isSubscription: true };
    }

    // Otherwise, calculate the regular subtotal
    const regularSubtotal = cartItems.reduce((acc, item) => {
      return acc + parseFloat(item.price.replace('$', '')) * item.quantity;
    }, 0).toFixed(2);

    const formattedTotal = regularSubtotal.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return { label: 'Cart totals', total: formattedTotal, isSubscription: false };
  };

  const { label, total, isSubscription } = calculateSubtotal();

  const updateCartCount = () => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
  };

  return (
    <>
      <Navbar cartCount={cartCount} updateCartCount={updateCartCount} />
      <div className="container mx-auto p-4 lg:mt-[70px] mt-[130px]">
        <h1 className="text-5xl text-[#007D44] font-bold mb-4 text-center">Cart</h1>
        <div className="flex flex-col md:flex-row justify-between mt-20 max-sm:mt-5">
          <div className="md:w-3/4 h-96 overflow-auto">
            {/* Header Row */}
            <div className="flex items-center font-bold border-b pb-2 mb-4 max-sm:hidden">
              <div className="ml-32">Product</div>
              <div className="ml-52">Price</div>
              {!isSubscription && <div className="ml-24 text-center">Quantity</div>}
              {!isSubscription && <div className="ml-24 text-center">Subtotal</div>}
            </div>

            {cartItems.length > 0 ? (
              <ul className="space-y-4">
                {cartItems.map((item, index) => (
                  <li key={index} className="flex items-center border-b py-7">
                    <button className="ml-4 text-red-500" onClick={() => removeItem(index)}>
                      <FaTimes />
                    </button>
                    <img src={item.img} alt={item.name} className="h-20 w-20 object-cover rounded-lg mr-4" />
                    <div className='flex max-sm:flex-col'>
                      <div className="w-52">
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-gray-500 mt-8">Brand: {item.brand}</p>
                      </div>
                      <p className="ml-16 max-sm:ml-0 max-sm:mt-3 w-10 flex items-center">
                        <p className='hidden max-sm:flex'>Price:</p> ${parseFloat(item.price.replace('$', '')).toFixed(2)}
                      </p>
                      {item.orderType !== "Large Box" && item.orderType !== "Small Box" && (
                        <div className='flex items-center mt-5'>
                          <p className='hidden max-sm:flex'>Quantity:</p>
                          <div className="flex items-center ml-20 max-sm:ml-3 rounded-lg border h-10">
                            <button
                              onClick={() => updateQuantity(index, Math.max(1, item.quantity - 1))}
                              className="px-2 py-1 text-gray-700"
                            >
                              -
                            </button>
                            <input
                              type="text"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(index, Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-12 text-center underline"
                            />
                            <button
                              onClick={() => updateQuantity(index, item.quantity + 1)}
                              className="px-2 py-1"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}
                      {item.orderType !== "Large Box" && item.orderType !== "Small Box" && (
                        <p className="ml-20 max-sm:ml-0 flex items-center">
                          <p className='hidden max-sm:flex'>Total:</p> ${(item.quantity * parseFloat(item.price.replace('$', ''))).toFixed(2)}
                        </p>
                      )}
                      {/* Display fixed total for Large Box or Small Box */}
                      {item.orderType === "Large Box" && (
                        <p className="ml-20 max-sm:ml-0 flex items-center">
                          <p className='hidden max-sm:flex'>Total:</p> $99.00
                        </p>
                      )}
                      {item.orderType === "Small Box" && (
                        <p className="ml-20 max-sm:ml-0 flex items-center">
                          <p className='hidden max-sm:flex'>Total:</p> $59.00
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Your cart is empty.</p>
            )}
          </div>
          <div className="md:w-2/5 max-sm:w-full mt-8 md:mt-0 flex justify-end">
            <div className="bg-gray-100 w-4/5 max-sm:w-full p-4 rounded-lg h-[300px] max-sm:h-[200px]">
              <h2 className="text-xl font-bold mb-4">{label}</h2>
              {!isSubscription && (
                <div className="flex justify-between mb-4">
                  <p>Subtotal:</p>
                  <p>${total}</p>
                </div>
              )}
              <div className="flex justify-between mb-4">
                <p>Total:</p>
                <p className="text-xl font-bold">${total}</p>
              </div>
              <Link to="/CheckoutPage">
                <button className="bg-[#007D44] mt-24 max-sm:mt-0 text-white py-2 w-full rounded">
                  Proceed to checkout
                </button>
              </Link>
            </div>
          </div>
        </div>
        <RecommendForYou />
      </div>
      <Footer />
    </>
  );
};

export default CartPage;